/**
 * The OTHER board: `values.attachments` from the graphs that have none.
 *
 * `?graph=peterhof_guide` is the live case today (a scripted ReAct graph that
 * speaks no A2UI) — an attachment is already a card, so the shim builds the
 * same catalogue components the canvas graph emits and hands them to the same
 * store. **It knows nothing about regions, children or the tree** beyond
 * appending an id to `region:main` — that is what keeps it a shim rather than
 * a second renderer.
 *
 * **`?graph=peterhof_agent` used this module too, until 18.09.2026** — the old
 * ReAct experiment that graph id used to name. It is now the Deep Agents
 * package's own `TurnCards`-only rendering (`sessionStore.hasNoBoard`), whose
 * state carries no `attachments` channel at all: `App.tsx` excludes it from
 * this path by name (`!hasNoBoard(assistantId)`) rather than leaving it to
 * `speaksA2ui` alone, since a card type this new package's plan does not
 * mention yet must not silently start drawing here.
 *
 * **It is called only for `peterhof_guide`, and `sessionStore.speaksA2ui`
 * (plus `hasNoBoard`) is the gate.** The canvas graph ships BOTH — its own
 * A2UI frames and the Telegram attachments riding the same turn — so running
 * this over its `values` drew the afisha twice on prod: `EventsList` from the
 * wire, an `ObjectStrip` under it from here. Two answers to «what is on the
 * board» is the drift this whole migration removed; the caller keeps them
 * from both speaking, and the reason the decision is the caller's is that
 * only it knows which graph is answering.
 *
 * Two rules it keeps from the old adapter, both of them measured:
 *
 *  * **`map_image` is dropped, never drawn** — its URL carries the 2GIS api key.
 *  * **`chips` go to the chat** as a `ChipGroup` of `Button`s, exactly the shape
 *    the canvas graph sends, so `Chips.tsx` has one reader and not two.
 */

import { useSurface, REGION_IDS, ROOT_ID } from '../a2ui/surfaceStore';
import type { A2uiComponent } from '../a2ui/types';
import { shortHash } from '../lib/ids';

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const text = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined;

const num = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

/** The four regions plus the root, so the tree exists before anything lands. */
function skeleton(): A2uiComponent[] {
  return [
    { id: ROOT_ID, component: 'BoardSurface', children: Object.values(REGION_IDS) },
    ...(Object.entries(REGION_IDS) as [keyof typeof REGION_IDS, string][]).map(
      ([region, id]) => ({ id, component: 'BoardRegion', region, children: [] }) as A2uiComponent,
    ),
  ];
}

/** Attachment rows → `TripPlan.days[0].items`. Only a stop carries a number. */
function planItems(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  const items: Record<string, unknown>[] = [];
  let stop = 0;
  for (const entry of raw) {
    if (!isObject(entry)) continue;
    const title = text(entry.title);
    if (!title) continue;
    const subtitle = text(entry.subtitle);
    const saved = Boolean(subtitle?.startsWith('📌')) || text(entry.section) === 'Отложенные места';
    if (!saved) stop += 1;
    items.push({
      kind: 'stop',
      ...(saved ? {} : { n: stop }),
      title,
      subtitle,
      note: text(entry.note),
      lat: num(entry.latitude),
      lon: num(entry.longitude),
      tickets_url: text(entry.tickets),
    });
  }
  return items;
}

/** Turns one turn's attachments into components and pushes them at the store. */
export function applyAttachments(attachments: unknown): void {
  if (!Array.isArray(attachments)) return;

  const surface = useSurface.getState();
  const nodes: A2uiComponent[] = surface.components[ROOT_ID] ? [] : skeleton();
  const mainIds = [...surface.childrenOf(REGION_IDS.main)];
  const footerIds = [...surface.childrenOf(REGION_IDS.footer)];
  const add = (node: A2uiComponent, into: string[]) => {
    nodes.push(node);
    if (!into.includes(node.id)) into.push(node.id);
  };

  for (const raw of attachments) {
    if (!isObject(raw)) continue;
    const type = text(raw.type);

    if (type === 'object_card') {
      const title = text(raw.title);
      if (!title) continue;
      add(
        {
          id: `obj:${shortHash(title)}`,
          component: 'ObjectCard',
          title,
          subtitle: text(raw.subtitle),
          text: text(raw.text),
          images: Array.isArray(raw.images) ? raw.images.filter((i) => typeof i === 'string') : [],
          // The attachment calls a button's caption `text`; the catalogue calls
          // it `label`. Translated at this one seam.
          links: Array.isArray(raw.buttons)
            ? raw.buttons
                .filter(isObject)
                .map((b) => ({ label: text(b.text), url: text(b.url) }))
                .filter((b) => b.label && b.url)
            : [],
          lat: num(raw.latitude),
          lon: num(raw.longitude),
        },
        mainIds,
      );
    } else if (type === 'trip_plan') {
      const fromSections = Array.isArray(raw.sections)
        ? raw.sections.filter(isObject).flatMap((s) => planItems(s.items))
        : [];
      const items = fromSections.length > 0 ? fromSections : planItems(raw.items);
      if (items.length === 0) continue;
      add(
        {
          id: 'plan:main',
          component: 'TripPlan',
          title: text(raw.title) ?? 'Мой план',
          totals: { stops: items.filter((i) => typeof i.n === 'number').length },
          days: [{ items }],
        },
        mainIds,
      );
    } else if (type === 'ui') {
      const options = Array.isArray(raw.options) ? raw.options.filter(isObject) : [];
      if (options.length === 0) continue;
      const layout = text(raw.layout) ?? 'chips';
      if (layout === 'chips') {
        const ids: string[] = [];
        for (const option of options) {
          const label = text(option.label);
          if (!label) continue;
          // The value goes on the wire as `userMessage` — byte for byte, the
          // same promise the canvas graph makes.
          const value = text(option.value) ?? label;
          const id = `chip:${shortHash(value)}`;
          ids.push(id);
          nodes.push(
            text(option.url)
              ? { id, component: 'LinkButton', label, url: text(option.url) }
              : {
                  id,
                  component: 'Button',
                  label,
                  action: { event: { name: 'chip_tap', userMessage: value } },
                },
          );
        }
        if (ids.length === 0) continue;
        add({ id: 'answers:turn', component: 'ChipGroup', title: text(raw.text), children: ids }, footerIds);
      } else {
        const used = new Set<string>();
        const tiles = options
          .map((option, index) => {
            const label = text(option.label) ?? '';
            let id = (text(option.value) ?? label ?? String(index)).slice(0, 64);
            if (used.has(id)) id = `${id.slice(0, 58)}#${index}`;
            used.add(id);
            return {
              id,
              label,
              sublabel: text(option.sublabel),
              image: text(option.image),
              value: text(option.value) ?? label,
              url: text(option.url),
            };
          })
          .filter((t) => t.label);
        if (tiles.length === 0) continue;
        add(
          {
            id: `strip:${shortHash(tiles.map((t) => t.id).join('|'))}`,
            component: 'ObjectStrip',
            title: text(raw.text),
            layout: layout === 'carousel' ? 'strip' : 'column',
            tiles: tiles.slice(0, 12),
          },
          mainIds,
        );
      }
    }
    // `object_list` duplicates the prose by contract; `map_image` carries a key.
  }

  if (nodes.length === 0) return;
  nodes.push(
    { id: REGION_IDS.main, component: 'BoardRegion', region: 'main', children: mainIds },
    { id: REGION_IDS.footer, component: 'BoardRegion', region: 'footer', children: footerIds },
  );
  useSurface.getState().apply([
    { version: 'v1.0', updateComponents: { surfaceId: surface.surfaceId ?? '', components: nodes } },
  ]);
}
